import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    ArrowRight, MessageCircle, Phone, Package, Wrench, Images, Clock,
    MapPin, Share2, QrCode, Instagram, Facebook, CreditCard, Globe, Check,
} from "lucide-react";

const PREVIEW_IMG = "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=900&q=80&auto=format&fit=crop";

// Thumbnails for the "no developer" website mockup — match the Royal Garments demo.
const NODEV_PRODUCTS = [
    { label: "Men's Wear", img: "https://images.unsplash.com/photo-1617137968427-85924c800a22?w=400&q=80" },
    { label: "Women's Wear", img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=400&q=80" },
    { label: "Kids Wear", img: "https://images.unsplash.com/photo-1519238263530-99bdd11df2ea?w=400&q=80" },
];

/* ---------- math helpers ---------- */
const clamp01 = (v) => Math.min(1, Math.max(0, v));
const lerp = (a, b, t) => a + (b - a) * t;
const sub = (p, a, b) => clamp01((p - a) / (b - a));
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

// Scroll-linked transform for a single layer between progress [a,b]
const layer = (p, a, b, { y = 30, x = 0, scale = 1 } = {}) => {
    const s = easeOut(sub(p, a, b));
    return {
        opacity: s,
        transform: `translate3d(${(1 - s) * x}px, ${(1 - s) * y}px, 0) scale(${lerp(scale, 1, s)})`,
        willChange: "transform, opacity",
    };
};

/* ---------- hooks ---------- */
function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => setReduced(mq.matches);
        update();
        mq.addEventListener?.("change", update);
        return () => mq.removeEventListener?.("change", update);
    }, []);
    return reduced;
}

function useIsDesktop() {
    const [d, setD] = useState(typeof window !== "undefined" ? window.matchMedia("(min-width:1024px)").matches : true);
    useEffect(() => {
        const mq = window.matchMedia("(min-width:1024px)");
        const on = () => setD(mq.matches);
        mq.addEventListener?.("change", on);
        return () => mq.removeEventListener?.("change", on);
    }, []);
    return d;
}

// Continuous scroll progress 0->1. mode "pin": tall section pinned; "enter": element entering view.
function useScrollProgress(ref, { mode = "pin", disabled = false } = {}) {
    const [p, setP] = useState(disabled ? 1 : 0);
    useEffect(() => {
        if (disabled) { setP(1); return; }
        const el = ref.current;
        if (!el) return;
        let raf = null;
        let attached = false;
        const compute = () => {
            raf = null;
            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight || 1;
            let prog;
            if (mode === "pin") {
                const total = rect.height - vh;
                prog = total > 0 ? (-rect.top) / total : (rect.top <= 0 ? 1 : 0);
            } else {
                prog = (vh - rect.top) / (vh * 0.8);
            }
            setP(clamp01(prog));
        };
        const onScroll = () => { if (raf == null) raf = requestAnimationFrame(compute); };
        const io = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                if (!attached) {
                    window.addEventListener("scroll", onScroll, { passive: true });
                    window.addEventListener("resize", onScroll, { passive: true });
                    attached = true;
                }
                compute();
            } else if (attached) {
                window.removeEventListener("scroll", onScroll);
                window.removeEventListener("resize", onScroll);
                attached = false;
            }
        }, { threshold: 0 });
        io.observe(el);
        compute();
        return () => {
            io.disconnect();
            if (attached) {
                window.removeEventListener("scroll", onScroll);
                window.removeEventListener("resize", onScroll);
            }
            if (raf) cancelAnimationFrame(raf);
        };
    }, [ref, mode, disabled]);
    return p;
}

/* ---------- shared UI frame ---------- */
function BrowserFrame({ p, children, url = "royal-garments.bizlinkk.com" }) {
    const shadow = lerp(0.05, 0.22, easeOut(sub(p, 0.25, 1)));
    const frame = {
        opacity: easeOut(sub(p, 0, 0.3)),
        transform: `translate3d(0, ${(1 - easeOut(sub(p, 0, 0.4))) * 44}px, 0) scale(${lerp(0.93, 1, easeOut(sub(p, 0, 0.45)))})`,
        boxShadow: `0 34px 70px -24px rgba(20,30,60,${shadow})`,
        willChange: "transform, opacity",
    };
    return (
        <div style={frame} className="relative rounded-2xl overflow-hidden glass-strong border border-border/60">
            <div className="h-9 flex items-center gap-1.5 px-3 border-b border-border/60 bg-secondary/40">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                <div className="ml-2 flex-1 h-5 rounded-full bg-background/70 text-[10px] text-muted-foreground flex items-center px-3 truncate">{url}</div>
            </div>
            <div className="relative">{children}</div>
        </div>
    );
}

/* ---------- section visuals ---------- */
function VisualProfile({ p }) {
    return (
        <BrowserFrame p={p}>
            <div style={layer(p, 0.15, 0.5, { y: 18, scale: 1.06 })} className="relative h-32 sm:h-40 bg-secondary overflow-hidden">
                <img src={PREVIEW_IMG} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="px-5 pb-5">
                <div style={layer(p, 0.3, 0.6, { y: 16, scale: 0.6 })} className="-mt-7 w-14 h-14 rounded-2xl bg-accent text-white flex items-center justify-center font-heading font-bold shadow-lg border-4 border-background">RG</div>
                <div style={layer(p, 0.35, 0.65, { y: 14 })} className="mt-2.5">
                    <div className="font-heading font-semibold leading-tight">Royal Garments</div>
                    <div className="text-xs text-muted-foreground">Clothing Store · Jammu</div>
                </div>
                <p style={layer(p, 0.5, 0.8, { y: 14 })} className="mt-3 text-xs text-muted-foreground leading-relaxed">Quality clothing for the whole family at honest prices.</p>
                <div className="mt-4 flex gap-2">
                    <div style={layer(p, 0.6, 0.85, { y: 14 })} className="flex-1 h-9 rounded-full bg-emerald-500 text-white text-xs font-medium flex items-center justify-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</div>
                    <div style={layer(p, 0.7, 0.95, { y: 14 })} className="flex-1 h-9 rounded-full glass text-xs font-medium flex items-center justify-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Call</div>
                </div>
            </div>
        </BrowserFrame>
    );
}

function VisualOneLink({ p }) {
    const items = [
        { icon: Package, label: "Products", a: 0.2, b: 0.45 },
        { icon: Wrench, label: "Services", a: 0.28, b: 0.5 },
        { icon: Images, label: "Gallery", a: 0.36, b: 0.58 },
        { icon: Clock, label: "Opening hours", a: 0.44, b: 0.66 },
        { icon: MapPin, label: "Location", a: 0.52, b: 0.74 },
        { icon: Phone, label: "Contact", a: 0.6, b: 0.82 },
    ];
    return (
        <BrowserFrame p={p}>
            <div className="p-5">
                <div style={layer(p, 0.1, 0.35, { y: 14 })} className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-accent text-white flex items-center justify-center text-xs font-bold">RG</div>
                    <div className="font-heading text-sm font-semibold">Everything in one place</div>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                    {items.map((it, i) => {
                        const Icon = it.icon;
                        return (
                            <div key={i} style={layer(p, it.a, it.b, { y: 34 })} className="glass rounded-xl p-3 flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg glass flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-accent" /></div>
                                <span className="text-xs font-medium truncate">{it.label}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </BrowserFrame>
    );
}

function VisualShare({ p }) {
    const chips = [
        { icon: MessageCircle, label: "WhatsApp", a: 0.25, b: 0.45 },
        { icon: Instagram, label: "Instagram", a: 0.32, b: 0.52 },
        { icon: Facebook, label: "Facebook", a: 0.39, b: 0.59 },
        { icon: Globe, label: "Google Business", a: 0.46, b: 0.66 },
        { icon: CreditCard, label: "Visiting Card", a: 0.53, b: 0.73 },
    ];
    return (
        <div className="relative">
            <div style={layer(p, 0, 0.3, { y: 30, scale: 0.96 })} className="glass-strong rounded-2xl p-5 border border-border/60">
                <div className="flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-accent shrink-0" />
                    <div className="flex-1 h-9 rounded-full bg-background/70 border border-border/60 text-xs flex items-center px-3 text-muted-foreground truncate">royal-garments.bizlinkk.com</div>
                    <div className="h-9 px-3 rounded-full bg-accent text-white text-xs flex items-center shrink-0">Copy</div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2.5">
                    {chips.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <div key={i} style={layer(p, c.a, c.b, { y: 30 })} className="glass rounded-xl p-2.5 flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg glass flex items-center justify-center shrink-0"><Icon className="w-3.5 h-3.5 text-accent" /></div>
                                <span className="text-[11px] font-medium truncate">{c.label}</span>
                            </div>
                        );
                    })}
                    <div style={layer(p, 0.6, 0.85, { y: 30 })} className="glass rounded-xl p-2.5 flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-foreground flex items-center justify-center shrink-0"><QrCode className="w-3.5 h-3.5 text-background" /></div>
                        <span className="text-[11px] font-medium truncate">QR Code</span>
                    </div>
                </div>
            </div>
            <div style={layer(p, 0.78, 1, { y: 20, x: 12 })} className="absolute -bottom-4 -right-2 glass rounded-xl px-3 py-2 flex items-center gap-2 shadow-lg">
                <div className="w-7 h-7 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0"><MessageCircle className="w-3.5 h-3.5 text-emerald-600" /></div>
                <div className="text-[11px] leading-tight"><div className="font-semibold">New enquiry</div><div className="text-muted-foreground">via your link</div></div>
            </div>
        </div>
    );
}

function VisualNoDev({ p }) {
    const points = [
        { t: "No hosting setup", a: 0.3, b: 0.5 },
        { t: "No developer needed", a: 0.42, b: 0.62 },
        { t: "No complicated maintenance", a: 0.54, b: 0.74 },
    ];
    return (
        <div className="relative">
            <BrowserFrame p={p}>
                <div style={layer(p, 0.1, 0.45, { y: 14, scale: 1.04 })} className="h-24 bg-secondary overflow-hidden">
                    <img src={PREVIEW_IMG} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                    <div className="font-heading text-sm font-semibold leading-tight">Royal Garments</div>
                    <div className="text-[11px] text-muted-foreground mb-3">Clothing Store · Jammu</div>
                    <div className="grid grid-cols-3 gap-2">
                        {NODEV_PRODUCTS.map((pr) => (
                            <div key={pr.label} className="rounded-lg overflow-hidden bg-secondary border border-border/50">
                                <div className="aspect-square overflow-hidden">
                                    <img src={pr.img} alt={pr.label} className="w-full h-full object-cover" />
                                </div>
                                <div className="px-1 py-1 text-[9px] font-medium truncate text-center text-foreground/80">{pr.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </BrowserFrame>
            <div className="mt-4 space-y-2">
                {points.map((pt, i) => (
                    <div key={i} style={layer(p, pt.a, pt.b, { y: 18, x: -14 })} className="glass rounded-xl px-4 py-2.5 flex items-center gap-2.5">
                        <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-emerald-600" /></div>
                        <span className="text-sm font-medium">{pt.t}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ---------- story section ---------- */
function StorySection({ eyebrow, title, description, cta, visual: Visual, align = "right", testid }) {
    const reduced = usePrefersReducedMotion();
    const isDesktop = useIsDesktop();
    const outerRef = useRef(null);
    const innerRef = useRef(null);
    const pinP = useScrollProgress(outerRef, { mode: "pin", disabled: reduced || !isDesktop });
    const enterP = useScrollProgress(innerRef, { mode: "enter", disabled: reduced || isDesktop });
    const p = reduced ? 1 : (isDesktop ? pinP : enterP);

    const textStyle = (a, b, y) => {
        const s = easeOut(sub(p, a, b));
        return { opacity: s, transform: `translate3d(0, ${(1 - s) * y}px, 0)`, willChange: "transform, opacity" };
    };

    return (
        <section ref={outerRef} className="relative lg:h-[185vh]" data-testid={testid}>
            <div ref={innerRef} className="lg:sticky lg:top-0 lg:h-screen flex items-center px-4 sm:px-6 py-16 lg:py-0">
                <div className="max-w-6xl mx-auto w-full grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div className={`min-w-0 ${align === "left" ? "lg:order-2" : ""}`}>
                        <div style={textStyle(0, 0.25, 12)} className="inline-flex items-center gap-2 rounded-full glass px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
                            {eyebrow}
                        </div>
                        <h2 style={textStyle(0.08, 0.5, 26)} className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08]">
                            {title}
                        </h2>
                        <p style={textStyle(0.3, 0.72, 20)} className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg">
                            {description}
                        </p>
                        {cta && (
                            <div style={textStyle(0.55, 0.92, 16)} className="mt-7">
                                <Link to={cta.to}>
                                    <Button size="lg" className="btn-accent rounded-full h-12 px-7" data-testid={`${testid}-cta`}>
                                        {cta.label} <ArrowRight className="ml-1 w-4 h-4" />
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                    <div className={`min-w-0 ${align === "left" ? "lg:order-1" : ""}`}>
                        <Visual p={p} />
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function StoryShowcase() {
    return (
        <div data-testid="story-showcase">
            <StorySection
                testid="story-1"
                eyebrow="Your online home"
                title={<>Your business deserves more than a <span className="text-accent">WhatsApp message</span>.</>}
                description="Turn scattered chats into a professional website that introduces your shop the moment someone taps your link — name, logo, photos and one-tap contact."
                cta={{ label: "Create your website", to: "/signup" }}
                visual={VisualProfile}
            />
            <StorySection
                testid="story-2"
                align="left"
                eyebrow="One simple link"
                title={<>Everything your customer needs. <span className="text-accent">One simple link</span>.</>}
                description="Products, services, gallery, opening hours, location and contact — all assembled on a single page that always stays up to date."
                visual={VisualOneLink}
            />
            <StorySection
                testid="story-3"
                eyebrow="Share anywhere"
                title={<>Share it <span className="text-accent">anywhere</span>.</>}
                description="WhatsApp, Instagram, Facebook, a QR code on your counter, your Google Business Profile or a visiting card — one link works everywhere."
                visual={VisualShare}
            />
            <StorySection
                testid="story-4"
                align="left"
                eyebrow="Built for owners"
                title={<>Made for businesses, <span className="text-accent">not developers</span>.</>}
                description="No hosting to configure, no developer to hire, no maintenance to worry about. You run your shop — we keep your website running."
                cta={{ label: "Start free", to: "/signup" }}
                visual={VisualNoDev}
            />
        </div>
    );
}
